import { useState } from 'react';

// json import removed - return plain objects in React Router v7
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Clock, Share2 } from 'lucide-react';
import { toast } from 'sonner';

import Header from '~/components/Header';
import TryForFreeButton from '~/components/TryForFreeButton';
import { createLogger } from '~/utils/logger.server';

export const handle = {
  title: 'How to Speak Confidently in Front of Others | Pavone Academy',
  description: 'Learn practical strategies to overcome nervousness, build speaking confidence, and communicate effectively in front of any audience. Expert tips for professionals.',
};

const logger = createLogger('AcademyArticleRoute');

export async function loader(args: LoaderFunctionArgs) {
  await logger.info('Academy article loaded');
  return Response.json({});
}

export const meta: MetaFunction = () => {
  return [
    { title: 'How to Speak Confidently in Front of Others - Pavone' },
    { 
      name: 'description', 
      content: 'Learn practical strategies to overcome nervousness, build speaking confidence, and communicate effectively in front of any audience. Expert tips backed by psychology.' 
    },
    { name: 'keywords', content: 'speak confidently, public speaking confidence, overcome speaking anxiety, confident communication, presentation skills, fear of public speaking' },
    { property: 'og:title', content: 'How to Speak Confidently in Front of Others' },
    { property: 'og:description', content: 'Learn practical strategies to overcome nervousness, build speaking confidence, and communicate effectively in front of any audience.' },
    { property: 'og:type', content: 'article' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'How to Speak Confidently in Front of Others' },
    { name: 'twitter:description', content: 'Learn practical strategies to overcome nervousness and build speaking confidence.' },
  ];
};

export default function HowToSpeakConfidentlyArticle() {
  const [isShared, setIsShared] = useState(false);

  const handleShare = async () => {
    const url = window.location.href;
    const title = 'How to Speak Confidently in Front of Others';
    const text = 'Check out this article on building speaking confidence:';

    try {
      // Try Web Share API first (works on mobile and modern browsers)
      if (navigator.share) {
        await navigator.share({
          title,
          text,
          url,
        });
        setIsShared(true);
        toast.success('Article shared!');
        // Reset after 2 seconds
        setTimeout(() => setIsShared(false), 2000);
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(url);
        setIsShared(true);
        toast.success('Link copied to clipboard!');
        // Reset after 2 seconds
        setTimeout(() => setIsShared(false), 2000);
      }
    } catch (error) {
      // User cancelled or error occurred
      if (error instanceof Error && error.name !== 'AbortError') {
        // Try clipboard as fallback if share failed
        try {
          await navigator.clipboard.writeText(url);
          setIsShared(true);
          toast.success('Link copied to clipboard!');
          setTimeout(() => setIsShared(false), 2000);
        } catch (clipboardError) {
          toast.error('Failed to share. Please copy the link manually.');
        }
      }
      // AbortError means user cancelled - don't show error
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <article className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link
            to="/academy"
            className="inline-flex items-center gap-2 text-brand-dark-blue hover:text-brand-gold transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Back to Academy</span>
          </Link>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 rounded-2xl overflow-hidden"
          >
            <img
              src="/images/academy/presentation.jpg"
              alt="Public speaking confidence"
              className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover"
            />
          </motion.div>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold text-brand-gold bg-brand-gold/10 px-3 py-1 rounded-full">
                Confidence
              </span>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock className="h-4 w-4" />
                <span>8 min read</span>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-brand-dark-blue mb-6 leading-tight">
              How to Speak Confidently in Front of Others
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              The practical guide to overcoming nervousness and communicating with genuine confidence — 
              without pretending to be someone you're not.
            </p>

            <div className="flex items-center justify-between py-4 border-y border-gray-200 mb-12">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Published:</span> November 2, 2024
              </div>
              <button
                onClick={handleShare}
                className={`flex items-center gap-2 text-sm transition-colors ${
                  isShared
                    ? 'text-brand-green'
                    : 'text-gray-600 hover:text-brand-dark-blue'
                }`}
              >
                {isShared ? (
                  <>
                    <Check className="h-4 w-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4" />
                    <span>Share</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            {/* Introduction */}
            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              I still remember the first time I had to speak in front of a room full of people. My hands were shaking. 
              My voice came out thin and uncertain. I could feel every eye on me, and all I wanted was to disappear. 
              If you've ever felt that way, you're not alone.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              The truth is, <strong>most people struggle with speaking confidently in front of others</strong> — whether 
              it's a presentation at work, a toast at a wedding, or even just speaking up in a meeting. But here's 
              what I've learned after years of working with professionals: confidence isn't something you're born with. 
              It's something you build, deliberately and over time.
            </p>

            {/* Section 1 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Why Speaking in Front of Others Feels So Hard
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Before we talk about solutions, let's understand what's actually happening when you feel nervous about 
              speaking. Your brain is trying to protect you. Public speaking triggers our primal fear of social rejection — 
              being judged, embarrassed, or excluded from the group. In ancient times, that could mean survival. Today, 
              it just means your nervous system is overreacting.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              When you stand up to speak, your body releases cortisol and adrenaline. Your heart races. Your palms sweat. 
              You might feel a little dizzy. This is your fight-or-flight response kicking in — and it's completely normal. 
              The problem isn't the feeling itself. The problem is how we interpret it.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              Most people think: "I'm nervous, which means I'm not good at this." But what if you reframed it as: 
              "I'm nervous, which means I care about doing well." That small shift changes everything.
            </p>

            {/* Section 2 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              The Foundation: Preparation That Actually Works
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Here's what no one tells you about confidence: <strong>it's not about being fearless. It's about being prepared.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              When I was coaching a sales director who struggled with presenting to executives, we didn't focus on 
              "being confident." We focused on knowing his material so well that he could talk about it naturally, 
              without relying on slides or notes. After three weeks of daily practice, he walked into that boardroom 
              and delivered the best presentation of his career. His secret? He had practiced it 47 times.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              How to Prepare the Right Way
            </h3>

            <ul className="space-y-4 mb-8 text-lg text-gray-700">
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Know your core message.</strong> If you could only say one thing, what would it be? 
                Build everything else around that.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Practice out loud.</strong> Not in your head. Out loud. Record yourself. Listen back. 
                You'll catch awkward phrasing, rushed sections, and unclear points.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Rehearse in the actual space.</strong> If possible, visit the room beforehand. Stand where 
                you'll stand. Get comfortable with the environment.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Prepare for questions.</strong> Think about what people might ask. Have thoughtful answers ready.</span>
              </li>
            </ul>

            <p className="text-lg leading-relaxed text-gray-700 mb-8 bg-brand-gold/5 p-6 rounded-lg border-l-4 border-brand-gold">
              💡 <strong>Pro tip:</strong> Use the "parking lot" technique. If you're worried about forgetting something, 
              write down your key points on a small card. You probably won't need it, but knowing it's there is like 
              a safety net for your brain.
            </p>

            {/* Section 3 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Mastering Your Body Language
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Here's something fascinating: your body language doesn't just affect how others perceive you — it affects 
              how you perceive yourself. Social psychologist Amy Cuddy's research showed that holding confident postures 
              for just two minutes can actually increase testosterone and decrease cortisol in your body.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              When you <strong>stand tall, make eye contact, and use open gestures</strong>, you're not faking confidence. 
              You're creating it.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Body Language Techniques That Work
            </h3>

            <div className="space-y-6 mb-8 text-lg text-gray-700">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Ground yourself physically</h4>
                <p>Plant your feet shoulder-width apart. Feel the ground beneath you. This simple act creates stability 
                and reduces nervous energy.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Slow down your movements</h4>
                <p>Nervous people move quickly — fidgeting, pacing, rushing. Confident people move deliberately. 
                Slow down. Take your time. Own your space.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Make genuine eye contact</h4>
                <p>Don't scan the room like a searchlight. Pick one person, hold their gaze for 3-5 seconds while 
                making a point, then move to someone else. This creates connection.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Use your hands naturally</h4>
                <p>Don't hide them. Don't over-gesture. Just let them move naturally as you speak. It adds energy 
                and emphasis to your words.</p>
              </div>
            </div>

            {/* Section 4 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              The Power of Your Voice
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              One of the biggest confidence killers is a weak, uncertain voice. You know the feeling — you start speaking 
              and your voice comes out shaky, too quiet, or rushed. Here's what helps.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              <strong>Breath is everything.</strong> Before you speak, take a deep breath from your diaphragm. Not a 
              shallow chest breath — a real, belly-expanding breath. This oxygenates your brain, steadies your nerves, 
              and gives your voice power.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Then, <strong>speak from your chest, not your throat.</strong> Place your hand on your chest and feel 
              the vibration as you speak. That's the sound of confidence. It's deeper, steadier, and carries further.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Vocal Techniques to Build Confidence
            </h3>

            <ul className="space-y-4 mb-8 text-lg text-gray-700">
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Lower your pitch slightly.</strong> High-pitched voices sound nervous. Drop your pitch 
                just a bit — it sounds more authoritative and calm.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Use pauses strategically.</strong> Don't rush to fill silence. Pause before key points. 
                Pause after important statements. It creates emphasis and gives people time to absorb what you've said.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Vary your pace and volume.</strong> Monotone delivery is boring. Speed up when you're 
                excited. Slow down when making important points. Get louder for emphasis, softer for intimacy.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Warm up your voice.</strong> Before speaking, hum. Do some lip trills. Say tongue twisters. 
                Get your vocal cords ready.</span>
              </li>
            </ul>

            {/* Section 5 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Handling Nerves in the Moment
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Even with all the preparation in the world, you'll still feel nervous sometimes. That's okay. 
              <strong>The goal isn't to eliminate nerves. The goal is to not let them control you.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              I once watched a CEO freeze mid-sentence during a keynote speech. The room went silent. Everyone could 
              feel the tension. Then he smiled, took a breath, and said: "You know what? I just completely blanked. 
              Give me a second." The room laughed. He gathered his thoughts. And he continued. His authenticity in 
              that moment made him more relatable, not less credible.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Quick Tactics for Managing Anxiety
            </h3>

            <div className="bg-brand-dark-blue/5 p-6 rounded-lg mb-8">
              <ul className="space-y-3 text-lg text-gray-700">
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">1.</span>
                  <span><strong>The 4-7-8 breathing technique:</strong> Breathe in for 4 counts, hold for 7, 
                  exhale for 8. Do this three times before speaking. It activates your parasympathetic nervous system.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">2.</span>
                  <span><strong>Progressive muscle relaxation:</strong> Tense and release each muscle group, 
                  starting from your toes up to your face. This releases physical tension.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">3.</span>
                  <span><strong>Reframe the sensation:</strong> Instead of "I'm nervous," try "I'm excited." 
                  The physical sensations are nearly identical — it's just the interpretation that differs.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">4.</span>
                  <span><strong>Focus outward, not inward:</strong> Stop monitoring how you feel. Focus on your 
                  audience. What do they need to hear? How can you help them?</span>
                </li>
              </ul>
            </div>

            {/* Section 6 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Building Long-Term Speaking Confidence
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Here's the hard truth: reading this article won't make you a confident speaker. Practice will. 
              But not just any practice — <strong>deliberate, consistent practice with feedback.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Think of confidence as a muscle. Every time you speak in front of others, you're doing a rep. 
              The more reps you do, the stronger that muscle becomes. Start small. Speak up in meetings. 
              Volunteer to give presentations. Join a speaking club like Toastmasters. Or better yet, use 
              tools that give you AI-powered feedback on your speaking patterns.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              The key is to make it a habit. Don't wait for the perfect moment. Don't wait until you feel ready. 
              You build confidence by doing the thing you're afraid of, over and over, until it's no longer scary.
            </p>

            {/* Section 7 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              The Mindset Shift That Changes Everything
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Here's what finally clicked for me: <strong>Speaking isn't about you. It's about your message.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              When you're focused on how you look, how you sound, whether people like you — you're trapped in your 
              own head. But when you shift your focus to serving your audience, something magical happens. The pressure 
              lifts. Because now it's not about being perfect. It's about being helpful.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              Ask yourself before any speaking opportunity: "What's the one thing I want this audience to walk away with?" 
              Then make that your North Star. Everything else — your nerves, your mistakes, your imperfect delivery — 
              becomes background noise.
            </p>

            {/* Conclusion */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Your Next Steps
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Learning to speak confidently in front of others isn't a destination. It's a journey. There will be days 
              when you nail it and days when you stumble. That's part of the process.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Start where you are. Pick one technique from this article and try it this week. Maybe it's recording yourself 
              practicing. Maybe it's using the 4-7-8 breathing technique before your next meeting. Maybe it's just speaking 
              up once more than you normally would.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              Small actions, repeated consistently, create lasting change. You've got this.
            </p>

            {/* CTA Box */}
            <div className="bg-gradient-to-br from-brand-dark-blue to-brand-gold p-8 rounded-2xl text-white my-12">
              <h3 className="text-2xl font-bold mb-4">
                Ready to Practice Your Speaking Skills?
              </h3>
              <p className="text-lg mb-6 text-white/90">
                Use Pavone to record yourself speaking, get AI-powered feedback on your delivery, pace, clarity, 
                and confidence. See exactly where you can improve — and track your progress over time.
              </p>
              <TryForFreeButton size="md" />
            </div>

            {/* FAQ Section */}
            <div className="mt-16 mb-12">
              <h2 className="text-3xl font-bold text-brand-dark-blue mb-8">
                Frequently Asked Questions
              </h2>

              <div className="space-y-6">
                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    How long does it take to become a confident speaker?
                  </h3>
                  <p className="text-lg text-gray-700">
                    It varies by person, but most people notice significant improvement after 10-15 practice sessions 
                    with feedback. The key is consistent practice — not perfection. Some feel more confident after just 
                    a few weeks, while others may take a few months to feel fully comfortable.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    What if I forget what I'm going to say mid-speech?
                  </h3>
                  <p className="text-lg text-gray-700">
                    First, pause and take a breath. It's okay to pause — it feels longer to you than to your audience. 
                    If you need a moment, you can say something like "Let me gather my thoughts for a second" or refer 
                    to your notes. Most importantly, don't apologize profusely. Just continue when you're ready. 
                    Your audience is more forgiving than you think.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    How can I stop my voice from shaking when I'm nervous?
                  </h3>
                  <p className="text-lg text-gray-700">
                    A shaky voice usually comes from shallow breathing. Practice deep diaphragmatic breathing before 
                    speaking. Also, speak slightly louder than feels natural — projecting your voice requires more breath 
                    support, which naturally steadies your voice. Warming up your voice beforehand with humming or 
                    vocal exercises also helps tremendously.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Is it normal to feel nervous even after years of public speaking?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Absolutely. Even professional speakers and performers experience nervousness. The difference is they've 
                    learned to channel that nervous energy into their performance rather than let it paralyze them. Many 
                    say they'd be worried if they didn't feel some nervousness — it means they care about doing well.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    What's the best way to practice public speaking at home?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Record yourself speaking on camera. Watch it back with a critical but kind eye. Notice your body 
                    language, vocal variety, pacing, and clarity. Practice in front of a mirror or family members. 
                    Use AI tools like Pavone that provide structured feedback on multiple aspects of your delivery. 
                    The key is getting objective feedback, not just practicing in your head.
                  </p>
                </div>
              </div>
            </div>

            {/* Long-tail Keywords Section (hidden from users, for SEO) */}
            <div className="sr-only">
              <h2>Related Topics and Keywords</h2>
              <ul>
                <li>how to speak with confidence in public</li>
                <li>overcoming fear of speaking in front of people</li>
                <li>confident speaking techniques for professionals</li>
                <li>how to stop being nervous when presenting</li>
                <li>building public speaking confidence step by step</li>
                <li>speaking confidently without anxiety</li>
                <li>presentation skills for introverts</li>
              </ul>
            </div>
          </motion.div>

          {/* Author/Date Footer */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-900">Pavone Academy</p>
                <p>Expert communication coaching and speaking tips</p>
              </div>
              <Link
                to="/academy"
                className="text-brand-dark-blue hover:text-brand-gold font-medium transition-colors"
              >
                Read More Articles →
              </Link>
            </div>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-[1080px] mx-auto px-4 flex flex-col items-center gap-3 md:flex-row md:justify-between">
          <p className="text-sm text-muted-foreground">
            © 2026 Pavone. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <a href="/privacy" className="hover:underline">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:underline">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

